import { type FC, type ReactNode } from 'react';

import { stepInteractionConstants } from 'dibs-tracking';
const { STEP_INTERACTION_GLOBAL_MESSAGE_BANNER_CLICK } = stepInteractionConstants;

import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

import { isPreview } from './helpers/previewHelpers';
import {
    trackMessageBanner,
    getNoteByDevice,
    type MessageItem,
    type Cta,
} from './helpers/sharedMessageHelpers';

import styles from './MessageBanner-style.scss';

type DangerousProps = {
    dangerouslySetInnerHTML: { __html: string };
};

const getDangerousProps = (value?: string | null): DangerousProps | null =>
    typeof value === 'string'
        ? {
              dangerouslySetInnerHTML: {
                  __html: value,
              },
          }
        : null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getChildren = (value: any): any | null => (typeof value !== 'string' ? value : null);

const getContent = (message?: string | null): ReactNode => {
    const dangerousProps = getDangerousProps(message);
    if (dangerousProps) {
        return <span className={styles.itemContent} {...getDangerousProps(message)} />;
    }
    return <span className={styles.itemContent}>{getChildren(message)}</span>;
};

const trackCtaClick = (messageItem: MessageItem): void => {
    if (!isPreview()) {
        trackMessageBanner({
            messageItem,
            action: 'banner cta clicked',
            step_interaction_name: STEP_INTERACTION_GLOBAL_MESSAGE_BANNER_CLICK,
        });
    }
};

const getCta = ({
    messageItem,
    cta,
}: {
    messageItem: MessageItem;
    cta?: Cta | null;
}): ReactNode => {
    if (!cta || !cta.url) {
        return null;
    }

    const onClick = (): void => trackCtaClick(messageItem);
    const props = {
        className: styles.ctaLink,
        href: handleLocaleUrl(cta.url, GLOBAL_CLIENT_ONLY_LOCALE) || '#',
        onClick,
        target: '_blank',
        rel: 'noopener noreferrer',
    };

    const dangerousProps = getDangerousProps(cta.text);
    if (dangerousProps) {
        // `<Link />` does not support `dangerouslySetInnerHTML`
        /* eslint-disable jsx-a11y/anchor-has-content,react/forbid-elements */
        return <a {...props} {...dangerousProps} />;
        /* eslint-enable jsx-a11y/anchor-has-content,react/forbid-elements */
    }
    // eslint-disable-next-line react/forbid-elements
    return <a {...props}>{getChildren(cta.text)}</a>;
};

const MessageBannerItem: FC<{
    messageItem: MessageItem;
    onFocus: () => void;
}> = ({ messageItem, onFocus }) => {
    const deviceNote = getNoteByDevice(messageItem);
    const { message, cta } = deviceNote?.content || {};

    return (
        <div className={styles.itemWrapper} onFocus={onFocus}>
            {/*
                keep space between both function calls
                prettier removes `{' '}`
             */}
            {getContent(message)} {getCta({ messageItem, cta })}
        </div>
    );
};

export default MessageBannerItem;
